import { Fragment, type MouseEvent, type ReactNode, type ReactElement, useState } from 'react';
import { MenuV2 } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { styled } from '@mui/material/styles';
import MuiTooltip from '@mui/material/Tooltip';
import { Icon16Spinner } from '@figma/fpl-icons/internal';
import * as stylex from '@stylexjs/stylex';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { MacCmdIcon } from '@/UI/Icons/MacCmdIcon';
import { Box } from '@/UI/Box/Box';
import { getOS } from '@/utils/general';
import {
  isCheckboxGroupMenuItem,
  isDividerMenuItem,
  isGroupMenuItem,
  isRadioGroupMenuItem,
  isSectionLabel,
  isSlot,
  type ActionMenuItem,
  type MenuAction,
} from '@/components/Menu/Actions';
import { createTrackedMenuAction } from '@/components/Menu/createTrackedMenuAction';
import { Tooltip } from '@/UI/Tooltip/Tooltip';

const rotate = stylex.keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
});

const styles = stylex.create({
  /**
   * `Icon16Spinner` is a static glyph — FPL's `LoadingSpinner` supplies the rotation from its
   * wrapper `div`. We spin the `<svg>` directly so the trail stays span-only (a `div` inside the
   * `<span>` trail is invalid HTML).
   */
  spinner: {
    transformOrigin: 'center',
    animationName: rotate,
    animationDuration: '1s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '4s',
      animationTimingFunction: 'steps(4)',
    },
  },
});

type MenuV2ItemsProps = {
  items: MenuAction[];
  /** Close the whole menu; used for `withLoading` items that resolve asynchronously. */
  requestClose: () => void;
};

// Weave's white brand highlight needs primary on-brand text for readable shortcuts.
// Surface supplies the active primary colors; preserve resting and disabled shortcut colors.
export const ShortcutContent = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  '&[data-collapse-check]': {
    color: 'white',
    '--fpl-icon-color': 'currentColor',
    // The 24px check path ends at x=16.5; align its visible tip with the trailing edge.
    '& svg[data-fpl-icon-size="24"]': {
      transform: 'translateX(7.5px)',
    },
  },
  '@media (hover: hover)': {
    '[role="menuitem"]:not([aria-disabled="true"]):hover &': {
      color: token.text,
      '--fpl-icon-color': token.icon,
    },
  },
  '[role="menuitem"]:not([aria-disabled="true"]):not([data-movement-type="pointer"]):focus-visible &': {
    color: token.text,
    '--fpl-icon-color': token.icon,
  },
});

function renderShortcut(item: ActionMenuItem, isMac: boolean): ReactNode {
  if (item.shortcutKey) {
    return (
      <MenuV2.Shortcut>
        <ShortcutContent>
          {isMac ? <MacCmdIcon width={10} height={10} /> : 'Ctrl'}
          {item.shortcutKey}
        </ShortcutContent>
      </MenuV2.Shortcut>
    );
  }
  if (!item.shortcut) return null;
  return (
    <MenuV2.Shortcut>
      <ShortcutContent>{item.shortcut}</ShortcutContent>
    </MenuV2.Shortcut>
  );
}

export function MenuV2Items({ items, requestClose }: MenuV2ItemsProps) {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const isMac = getOS() === 'Mac';

  const onItemClick = async (
    e: MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    item: ActionMenuItem,
    key: string,
  ) => {
    e.stopPropagation();
    try {
      const action = createTrackedMenuAction(item);
      if (item.withLoading) {
        setLoadingKey(key);
        await action(e as MouseEvent<HTMLElement>);
      } else {
        void action(e as MouseEvent<HTMLElement>);
      }
    } finally {
      setLoadingKey(null);
    }
    // `withLoading` items keep the menu open during the async action (closeOnClick is
    // disabled below), so dismiss explicitly once it settles.
    if (item.withLoading) requestClose();
  };

  const renderItem = (item: MenuAction, key: string): ReactNode => {
    // `section_label` is unsupported flag-on: MenuV2 has no standalone label. Headings come from the
    // built-in `MenuV2.Group` / `CheckboxGroup` / `RadioGroup` titles (see the `group`/`checkbox_group`
    // / `radio_group` variants). A stray `section_label` simply renders nothing here.
    if (isSectionLabel(item)) {
      return null;
    }

    if (isDividerMenuItem(item)) {
      return <MenuV2.Separator key={key} />;
    }

    // Slots render their element verbatim so composite content (e.g. `ParentMenuItem` rendering a
    // `MenuV2.SubMenu`) stays a direct child of the menu.
    if (isSlot(item)) {
      return <Fragment key={key}>{item.el}</Fragment>;
    }

    // A titled cluster of plain rows → FPL's built-in group title (correct indent + single border).
    if (isGroupMenuItem(item)) {
      return (
        <MenuV2.Group key={key} title={item.title} aria-label={item.ariaLabel}>
          {item.items.map((child, childIndex) => renderItem(child, `${key}-${childIndex}`))}
        </MenuV2.Group>
      );
    }

    // Multi-select group: stays open on toggle and carries a built-in title.
    if (isCheckboxGroupMenuItem(item)) {
      return (
        <MenuV2.CheckboxGroup
          key={key}
          title={item.title}
          aria-label={item.ariaLabel}
          selectedItems={item.selectedValues}
          onChange={(values, { event }) => item.onChange(values, event as unknown as MouseEvent<HTMLElement>)}
        >
          {item.options.map((opt) => (
            <MenuV2.CheckboxGroupItem
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              lead={opt.icon ?? undefined}
            >
              {opt.label}
            </MenuV2.CheckboxGroupItem>
          ))}
        </MenuV2.CheckboxGroup>
      );
    }

    // Single-select: `RadioGroup` closes on select per FPL semantics.
    if (isRadioGroupMenuItem(item)) {
      return (
        <MenuV2.RadioGroup
          key={key}
          aria-label={item.ariaLabel}
          title={item.title}
          value={item.value}
          onChange={(value, { event }) => item.onChange(value, event as unknown as MouseEvent<HTMLElement>)}
        >
          {item.options.map((opt) => (
            <MenuV2.RadioGroupItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </MenuV2.RadioGroupItem>
          ))}
        </MenuV2.RadioGroup>
      );
    }

    const isLoading = loadingKey === key;
    const trail = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {isLoading ? <Icon16Spinner aria-hidden={true} {...stylex.props(styles.spinner)} /> : item.endContent}
        {renderShortcut(item, isMac)}
        {item.selected ? (
          item.collapsePresentation ? (
            <ShortcutContent data-collapse-check>
              <CheckMarkIcon />
            </ShortcutContent>
          ) : (
            <CheckMarkIcon />
          )
        ) : null}
      </Box>
    );

    // Keep `MenuV2.Item` as the composed menu child (do not wrap it). Put the tooltip on the
    // label so ToggleTip doesn't break MenuV2's parent/child compose contract.
    const label =
      item.disabled && item.disabledTooltip ? (
        item.collapsePresentation ? (
          <CursorMenuTooltip title={item.disabledTooltip}>
            <Box component="span" sx={{ display: 'inline-flex', width: '100%', pointerEvents: 'auto' }}>
              {item.name}
            </Box>
          </CursorMenuTooltip>
        ) : (
          <Tooltip title={item.disabledTooltip} placement="left">
            <Box component="span" sx={{ display: 'inline-flex', maxWidth: '100%', pointerEvents: 'auto' }}>
              {item.name}
            </Box>
          </Tooltip>
        )
      ) : (
        item.name
      );

    return (
      <MenuV2.Item
        key={key}
        lead={item.icon ?? undefined}
        trail={trail}
        disabled={item.disabled}
        // `withLoading` items must not close on click; we close them after the action resolves.
        closeOnClick={!item.withLoading}
        onClick={(e) => void onItemClick(e, item, key)}
      >
        {label}
      </MenuV2.Item>
    );
  };

  return items.map((item, index) => renderItem(item, String(index)));
}

/** Shared by the collapse row in both menu implementations. */
export function CursorMenuTooltip({ title, children }: { title: ReactNode; children: ReactElement }) {
  return (
    <MuiTooltip title={title} placement="top" followCursor>
      {children}
    </MuiTooltip>
  );
}
